/**
 * Counts available pick types for the badge so it matches what the UI shows:
 * - 1X2, BTTS, Double Chance, etc. = 1 each
 * - Handicap = number of lines (each line with 3 outcomes)
 * - Over/Under goals & first half = number of lines
 * - Corners = number of lines (Over/Under pairs)
 */

import {
  MARKET_IDS,
  HANDICAP_MARKET_IDS,
  MATCH_DETAILS_TABS,
  TAB_MARKET_IDS,
} from '../constants/matchDetails.constants';
import { groupLegacyHandicapResultValuesByLine } from '../../../components/create/MatchDetailsScreen/overUnderGrouping.helper';
import type { MarketOddValue } from '../types/create.types';

/** API market shape (id + values). */
type MarketInput = { id: number; values?: Array<{ value: string; odd: number }> };

/** Only count markets that the UI actually shows (in any tab). Avoids over-counting API-only markets (e.g. Asian Handicap). */
const DISPLAYABLE_MARKET_IDS = new Set<number>([
  ...TAB_MARKET_IDS[MATCH_DETAILS_TABS.MAIN],
  ...TAB_MARKET_IDS[MATCH_DETAILS_TABS.GOALS],
  ...TAB_MARKET_IDS[MATCH_DETAILS_TABS.SCORES],
  ...TAB_MARKET_IDS[MATCH_DETAILS_TABS.STATS],
]);

/** Market IDs that count as 1 each (single pick type). */
const SINGLE_MARKET_IDS: readonly number[] = [
  MARKET_IDS.MATCH_WINNER,
  MARKET_IDS.DOUBLE_CHANCE,
  MARKET_IDS.BTTS,
  MARKET_IDS.FIRST_HALF_WINNER,
  MARKET_IDS.SECOND_HALF_WINNER,
  MARKET_IDS.ODD_EVEN,
  MARKET_IDS.EXACT_GOALS,
  MARKET_IDS.CORRECT_SCORE,
  MARKET_IDS.HALF_TIME_FULL_TIME,
  MARKET_IDS.HALF_TIME_CORRECT_SCORE,
  MARKET_IDS.CARDS,
  MARKET_IDS.CLEAN_SHEET_HOME,
  MARKET_IDS.CLEAN_SHEET_AWAY,
  MARKET_IDS.HIGHEST_SCORING_HALF,
];

/** Over/Under goals (1.5, 2.5, 3.5) – each line = 1. */
const OU_GOALS_IDS: readonly number[] = [
  MARKET_IDS.OVER_UNDER_15,
  MARKET_IDS.OVER_UNDER_25,
  MARKET_IDS.OVER_UNDER_35,
];

/** Over/Under first half – each line = 1. */
const OU_FIRST_HALF_IDS: readonly number[] = [
  MARKET_IDS.OVER_UNDER_05_1ST_HALF,
  MARKET_IDS.OVER_UNDER_15_1ST_HALF,
  MARKET_IDS.OVER_UNDER_25_1ST_HALF,
];

/**
 * Returns the number of available pick types for the badge, matching UI:
 * 1X2=1, BTTS=1, Double Chance=1, First/Second Half Winner=1 each, Halftime/Fulltime=1, Handicap=#lines,
 * O/U goals=#lines, O/U first half=#lines, Exact Goals=1, Correct Score=1,
 * HT Correct Score=1, Odd/Even=1, Cards=1, Clean Sheet Home/Away=1 each, Corners=#lines (9.5, 10.5, …).
 */
export function getDisplayAvailablePicksCount(markets: MarketInput[] | null | undefined): number {
  if (!Array.isArray(markets)) return 0;
  let count = 0;
  for (const m of markets) {
    if (!DISPLAYABLE_MARKET_IDS.has(m.id)) continue;
    const values = Array.isArray(m.values) ? m.values : [];
    if (SINGLE_MARKET_IDS.includes(m.id)) {
      if (values.length > 0) count += 1;
      continue;
    }
    if (m.id === MARKET_IDS.CORNERS) {
      count += Math.floor(values.length / 2);
      continue;
    }
    if (OU_GOALS_IDS.includes(m.id) || OU_FIRST_HALF_IDS.includes(m.id)) {
      if (values.length > 0) count += 1;
      continue;
    }
    if (m.id === MARKET_IDS.HANDICAP_RESULT) {
      const rows = groupLegacyHandicapResultValuesByLine(values as MarketOddValue[]);
      count += rows.length;
      continue;
    }
    if ((HANDICAP_MARKET_IDS as readonly number[]).includes(m.id)) {
      if (values.length === 3) count += 1;
      continue;
    }
    if (values.length > 0) count += 1;
  }
  return count;
}
